import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * PuzzleHouseHero
 *
 * A house silhouette filled with individual, brand-colored puzzle pieces that
 * animate in as the user's "campaign progress" grows. Includes a live counter
 * ("Pieces to Go") and a call-to-action slot for "Donate a Piece".
 *
 * Pure SVG + IntersectionObserver.
 */

const PUZZLE_D =
  "M2,8 C2,4.7 4.7,2 8,2 L17,2 C17.5,2 18,1.4 18,0.8 C18,-2 22,-2 22,0.8 C22,1.4 22.5,2 23,2 L32,2 C35.3,2 38,4.7 38,8 L38,17 C38,17.5 38.6,18 39.2,18 C42,18 42,22 39.2,22 C38.6,22 38,22.5 38,23 L38,32 C38,35.3 35.3,38 32,38 L23,38 C22.5,38 22,38.6 22,39.2 C22,42 18,42 18,39.2 C18,38.6 17.5,38 17,38 L8,38 C4.7,38 2,35.3 2,32 L2,23 C2,22.5 1.4,22 0.8,22 C-2,22 -2,18 0.8,18 C1.4,18 2,17.5 2,17 Z";

const BRAND = ["#2196F3", "#E53935", "#FFB300", "#4CAF50"];

// House polygon in a 480 x 340 space (roof + walls).
// Roof triangle apex: (240, 40); base: (60, 170) - (420, 170)
// Walls rectangle: (80, 170) - (400, 320)
const HOUSE_POLY = [
  [240, 40],  // roof apex
  [420, 170], // right eave
  [400, 170], // wall top right
  [400, 320], // bottom right
  [80, 320],  // bottom left
  [80, 170],  // wall top left
  [60, 170],  // left eave
];

function pointInPolygon(x, y, poly) {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0], yi = poly[i][1];
    const xj = poly[j][0], yj = poly[j][1];
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

const PIECE = 26; // 26px per puzzle cell inside the house

const buildHousePieces = () => {
  const pieces = [];
  // Door + window openings (holes we skip)
  const door = { x: 220, y: 240, w: 40, h: 80 };
  const winL = { x: 120, y: 210, w: 50, h: 50 };
  const winR = { x: 310, y: 210, w: 50, h: 50 };
  const inHole = (cx, cy, r) =>
    cx >= r.x && cx <= r.x + r.w && cy >= r.y && cy <= r.y + r.h;

  for (let y = 30; y < 330; y += PIECE) {
    for (let x = 40; x < 440; x += PIECE) {
      const cx = x + PIECE / 2;
      const cy = y + PIECE / 2;
      if (!pointInPolygon(cx, cy, HOUSE_POLY)) continue;
      if (inHole(cx, cy, door) || inHole(cx, cy, winL) || inHole(cx, cy, winR))
        continue;
      const color = BRAND[(Math.floor(x / 26) + Math.floor(y / 26) * 3) % BRAND.length];
      // "distance from door" for reveal ordering
      const dist = Math.hypot(cx - 240, cy - 280);
      pieces.push({ id: `${x}-${y}`, x, y, color, dist });
    }
  }
  pieces.sort((a, b) => a.dist - b.dist);
  return pieces;
};

export default function PuzzleHouseHero({
  progress = 0.72,          // 0..1 how much of the house is built
  totalGoal = 8400,         // total pieces goal (representing dollars, etc.)
  className = "",
}) {
  const [active, setActive] = useState(false);
  const [displayProgress, setDisplayProgress] = useState(0);
  const pieces = useMemo(() => buildHousePieces(), []);
  const rootRef = useRef(null);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(true)),
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Animate the counter up when active
  useEffect(() => {
    if (!active) return;
    let raf;
    const start = performance.now();
    const durationMs = 1600;
    const step = (t) => {
      const p = Math.min(1, (t - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplayProgress(eased * progress);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active, progress]);

  const built = Math.round(pieces.length * displayProgress);
  const remainingPieces = pieces.length - built;
  const piecesGoal = totalGoal;
  const piecesRaised = Math.round(piecesGoal * displayProgress);
  const piecesToGo = Math.max(0, piecesGoal - piecesRaised);

  return (
    <div
      ref={rootRef}
      className={`relative ${className}`}
      data-testid="puzzle-house-hero"
    >
      {/* Soft glow */}
      <div
        className="absolute inset-0 -z-10 rounded-[3rem]"
        style={{
          background:
            "radial-gradient(1200px 400px at 50% 100%, rgba(255,179,0,0.10), transparent 60%), radial-gradient(800px 300px at 30% 40%, rgba(33,150,243,0.10), transparent 60%)",
        }}
        aria-hidden
      />

      <svg
        viewBox="0 0 480 400"
        className="w-full h-auto"
        role="img"
        aria-label="A house made of puzzle pieces representing our community goal"
      >
        <defs>
          <filter id="pieceShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="1.5" stdDeviation="1.2" floodColor="#1F2937" floodOpacity="0.18" />
          </filter>
          <linearGradient id="ground" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#FDFBF7" stopOpacity="0" />
            <stop offset="1" stopColor="#F4F1EA" stopOpacity="1" />
          </linearGradient>
        </defs>

        {/* Ground */}
        <rect x="0" y="320" width="480" height="60" fill="url(#ground)" />
        <line x1="30" y1="322" x2="450" y2="322" stroke="#1F2937" strokeOpacity="0.12" strokeWidth="1" />

        {/* Ghost house silhouette (unbuilt outline) */}
        <polygon
          points={HOUSE_POLY.map((p) => p.join(",")).join(" ")}
          fill="#F4F1EA"
          stroke="#1F2937"
          strokeOpacity="0.10"
          strokeWidth="1.5"
          strokeDasharray="4 4"
        />

        {/* Door + windows (ghost) */}
        <rect x="220" y="240" width="40" height="80" fill="#FDFBF7" stroke="#1F2937" strokeOpacity="0.12" />
        <circle cx="252" cy="282" r="1.6" fill="#1F2937" opacity="0.4" />
        <rect x="120" y="210" width="50" height="50" fill="#FDFBF7" stroke="#1F2937" strokeOpacity="0.12" />
        <line x1="145" y1="210" x2="145" y2="260" stroke="#1F2937" strokeOpacity="0.10" />
        <line x1="120" y1="235" x2="170" y2="235" stroke="#1F2937" strokeOpacity="0.10" />
        <rect x="310" y="210" width="50" height="50" fill="#FDFBF7" stroke="#1F2937" strokeOpacity="0.12" />
        <line x1="335" y1="210" x2="335" y2="260" stroke="#1F2937" strokeOpacity="0.10" />
        <line x1="310" y1="235" x2="360" y2="235" stroke="#1F2937" strokeOpacity="0.10" />

        {/* Built puzzle pieces */}
        {pieces.slice(0, built).map((p, i) => (
          <g
            key={p.id}
            transform={`translate(${p.x}, ${p.y}) scale(${PIECE / 40})`}
            style={{
              filter: "url(#pieceShadow)",
              opacity: 0,
              animation: `piecePop 500ms ease-out both`,
              animationDelay: `${Math.min(i * 10, 900)}ms`,
            }}
          >
            <path d={PUZZLE_D} fill={p.color} stroke="#FDFBF7" strokeWidth="1" fillOpacity="0.96" />
          </g>
        ))}

        {/* Foundation label */}
        <g transform="translate(240 344)">
          <rect x="-60" y="-14" width="120" height="26" rx="13" fill="#FFFFFF" stroke="#1F2937" strokeOpacity="0.08" />
          <text x="0" y="4" textAnchor="middle" fontFamily="Outfit, sans-serif" fontWeight="700" fontSize="12" fill="#1F2937">
            Marion, Arkansas
          </text>
        </g>

        <style>{`
          @keyframes piecePop {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }
        `}</style>
      </svg>

      {/* Counter card */}
      <div
        data-testid="puzzle-house-progress"
        className="mx-auto -mt-6 md:-mt-8 relative z-10 max-w-md rounded-3xl bg-white border border-black/5 shadow-sm px-6 py-5 flex items-center gap-5"
      >
        <div className="flex-1">
          <div className="text-[0.7rem] uppercase tracking-[0.2em] font-semibold text-[#6B7280]">Pieces to Go</div>
          <div className="mt-1 font-heading text-4xl font-bold text-[#1F2937] tabular-nums">
            {piecesToGo.toLocaleString()}
          </div>
          <div className="mt-1 text-xs text-[#6B7280]">
            {piecesRaised.toLocaleString()} of {piecesGoal.toLocaleString()} raised
          </div>
        </div>
        <div className="w-24 h-24 relative">
          <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#F4F1EA" strokeWidth="3" />
            <circle
              cx="18" cy="18" r="15.9"
              fill="none"
              stroke="#2196F3"
              strokeWidth="3"
              strokeDasharray={`${displayProgress * 100} 100`}
              strokeLinecap="round"
              style={{ transition: "stroke-dasharray 250ms" }}
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center font-heading font-bold text-[#1F2937] tabular-nums">
            {Math.round(displayProgress * 100)}%
          </div>
        </div>
      </div>
    </div>
  );
}
